import JSONViewer from '@/components/common/debug/JSONViewer';
import ErrorNodeWrapper from '@/components/validation/ErrorNodeWrapper';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { Application } from '@/types/application';
import { Detail } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { ValidationError } from '@/types/validation';
import { isObject, isString } from '@/util/typeGuards';
import { argumentsUtil } from '@/util/validation/arguments';
import { enumUtil } from '@/util/validation/enum';
import { kvValueListUtil } from '@/util/validation/kvvaluelist';
import { kvVariablesListUtil } from '@/util/validation/kvvariableslist';
import { multipleUtil } from '@/util/validation/multiple';
import { promptlistUtil } from '@/util/validation/promptlist';
import { queueUtil } from '@/util/validation/queue';
import { queueMessageUtil } from '@/util/validation/queuemessage';
import { returnvaluesUtil } from '@/util/validation/returnvalues';
import { returnvariablesUtil } from '@/util/validation/returnvariables';
import { semaphoreUtil } from '@/util/validation/semaphore';
import { signalUtil } from '@/util/validation/signal';
import { subroutineUtil } from '@/util/validation/subroutines';
import { synceventUtil } from '@/util/validation/syncevent';
import { textblockUtil } from '@/util/validation/textblock';
import { valueUtil } from '@/util/validation/value';
import { valueListUtil } from '@/util/validation/valuelist';
import { variableUtil } from '@/util/validation/variable';
import { variablesListUtil } from '@/util/validation/variableslist';
import Typography from '@mui/material/Typography';
import { FC } from 'react';
import ParamArguments from './ParamArguments';
import ParamEnum from './ParamEnum';
import ParamKvValueList from './ParamKvValueList';
import ParamKvVariablesList from './ParamKvVariablesList';
import ParamMessageQueue from './ParamMessageQueue';
import ParamMultiple from './ParamMultiple';
import ParamPromptList from './ParamPromptList';
import ParamQueue from './ParamQueue';
import ParamQueueValue from './ParamQueueValue';
import ParamQueueVariable from './ParamQueueVariable';
import ParamReturnValues from './ParamReturnValues';
import ParamReturnVariables from './ParamReturnVariables';
import ParamSemaphore from './ParamSemaphore';
import ParamSignal from './ParamSignal';
import ParamStringInteger from './ParamStringInteger';
import ParamSubroutine from './ParamSubroutine';
import ParamSyncEvent from './ParamSyncEvent';
import ParamTextField from './ParamTextField';
import ParamUserEvent from './ParamUserEvent';
import ParamValueList from './ParamValueList';
import ParamVariable from './ParamVariable';
import ParamVariableList from './ParamVariableList';

export type ChangeProps = {
  configValue: string;
  newValue: unknown;
};

export type ParamRendererProps = {
  nodeId: number;
  parentId: number;
  configuration: Configuration;
  initialValue?: any;
  onChange: (...args: [string, unknown][]) => void;
};

export type ParamProps = ParamRendererProps & {
  errors?: ValidationError[];
};

const ParamRenderer: FC<ParamProps> = (props) => {
  const { application } = useApplication();
  const { metadata } = useMetadata();

  const node = application.blocks.nodes[props.nodeId];
  const blockName = `${props.nodeId}: ${node.type}${node.label && ` (${node.label})`}`;

  const errorArgs: [Application, Metadata, number, number, string, Configuration, Detail] = [
    application,
    metadata,
    props.parentId,
    props.nodeId,
    blockName,
    props.configuration,
    props.initialValue as Detail
  ];

  switch (props.configuration.type) {
    case 'value':
      return (
        <ErrorNodeWrapper errors={valueUtil().validate(...errorArgs)}>
          <ParamStringInteger
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'textblock':
      return (
        <ErrorNodeWrapper errors={textblockUtil().validate(...errorArgs)}>
          <ParamTextField
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'enum':
      return (
        <ErrorNodeWrapper
          errors={enumUtil().validateBasic(blockName, props.configuration, props.initialValue as Detail)}
        >
          <ParamEnum
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'prompts':
      return (
        <ErrorNodeWrapper errors={promptlistUtil().validate(...errorArgs)}>
          <ParamPromptList
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'multiple':
      return (
        <ErrorNodeWrapper
          errors={multipleUtil().validateBasic(blockName, props.configuration, props.initialValue as Detail)}
        >
          <ParamMultiple
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'variable':
      return (
        <ErrorNodeWrapper errors={variableUtil().validate(...errorArgs)}>
          <ParamVariable
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'variableslist':
      return (
        <ErrorNodeWrapper errors={variablesListUtil().validate(...errorArgs)}>
          <ParamVariableList
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'valuelist':
      return (
        <ErrorNodeWrapper errors={valueListUtil().validate(...errorArgs)}>
          <ParamValueList
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'kvvariableslist':
      return (
        <ErrorNodeWrapper errors={kvVariablesListUtil().validate(...errorArgs)}>
          <ParamKvVariablesList
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'kvvaluelist':
      return (
        <ErrorNodeWrapper errors={kvValueListUtil().validate(...errorArgs)}>
          <ParamKvValueList
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'subroutine':
    case 'eventhandler':
      return (
        <ErrorNodeWrapper errors={subroutineUtil().validate(...errorArgs)}>
          <ParamSubroutine
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'queue':
      return (
        <ErrorNodeWrapper errors={queueUtil().validate(...errorArgs)}>
          <ParamQueue
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'queuemessage':
      return (
        <ErrorNodeWrapper errors={queueMessageUtil().validate(...errorArgs)}>
          <ParamMessageQueue
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'queuevalue':
      return (
        <ErrorNodeWrapper errors={valueUtil().validate(...errorArgs)}>
          <ParamQueueValue
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'queuevariable':
      return (
        <ErrorNodeWrapper errors={variableUtil().validate(...errorArgs)}>
          <ParamQueueVariable
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'userevent':
      return (
        <ErrorNodeWrapper errors={valueUtil().validate(...errorArgs)}>
          <ParamUserEvent
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'arguments':
      return (
        <ErrorNodeWrapper errors={argumentsUtil().validate(...errorArgs)}>
          <ParamArguments
            nodeId={props.nodeId}
            parentId={props.parentId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
          />
        </ErrorNodeWrapper>
      );

    case 'returnvariables':
      return (
        <ErrorNodeWrapper errors={returnvariablesUtil().validate(...errorArgs)}>
          <ParamReturnVariables
            nodeId={props.nodeId}
            parentId={props.parentId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
          />
        </ErrorNodeWrapper>
      );

    case 'returnvalues':
    case 'appreturnvalues':
      return (
        <ErrorNodeWrapper errors={returnvaluesUtil().validate(...errorArgs)}>
          <ParamReturnValues
            nodeId={props.nodeId}
            parentId={props.parentId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
          />
        </ErrorNodeWrapper>
      );

    case 'signal':
      return (
        <ErrorNodeWrapper errors={signalUtil().validate(...errorArgs)}>
          <ParamSignal
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'semaphore':
      return (
        <ErrorNodeWrapper errors={semaphoreUtil().validate(...errorArgs)}>
          <ParamSemaphore
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'syncevent':
      return (
        <ErrorNodeWrapper errors={synceventUtil().validate(...errorArgs)}>
          <ParamSyncEvent
            nodeId={props.nodeId}
            configuration={props.configuration}
            onChange={props.onChange}
            initialValue={props.initialValue}
            parentId={props.parentId}
          />
        </ErrorNodeWrapper>
      );

    case 'passthrough': {
      const initValue = props.initialValue as object;
      const prop = props.configuration.value;
      const data = initValue && isString(prop) && isObject(initValue) && initValue[prop];
      return data ? <JSONViewer title={props.configuration.value} src={data} collapsed /> : <></>;
    }

    default:
      return (
        <Typography variant='body1' fontFamily='monospace'>
          TODO:
          {JSON.stringify({
            [props.configuration.value]: props.configuration.type
          })}
        </Typography>
      );
  }
};

export default ParamRenderer;
